import {
  BuildCircle as BuildCircleIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ErrorOutlineOutlined as ErrorOutlineOutlinedIcon,
  RateReviewOutlined as RateReviewOutlinedIcon,
  Send as SendIcon,
  ThumbDownAltOutlined as ThumbDownAltOutlinedIcon,
  ThumbUpAltOutlined as ThumbUpAltOutlinedIcon,
} from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { IconClipboard } from '@tabler/icons'
import { isEmpty } from 'lodash'
import React, { CSSProperties, MouseEventHandler, ReactNode, useRef, useState } from 'react'

import InfoCircled from '@/icons/InfoCircled'
import { getTaskBody, getTitle } from '@/utils/agentWebsocket/helpers'
import { getMessageAction } from '@/utils/chatflow/helpers'
import { StyledButton } from '@components/ui/button/StyledButton'
import ChatflowFileChip from '@components/PromptBox/PromptBox.ChatflowFileChip'
import { MessageFeedbackValue } from '@api/agentBuilder/chatmessage'
import {
  MESSAGE_TYPE_API,
  MESSAGE_TYPE_FINAL_ANSWER,
  MESSAGE_TYPE_FINAL_ANSWER_STREAM,
  MESSAGE_TYPE_INTERMEDIATE_ANSWER,
  MESSAGE_TYPE_INTERMEDIATE_ANSWER_STREAM,
  MESSAGE_TYPE_LLM_ERROR,
  MESSAGE_TYPE_TASK,
  MESSAGE_TYPE_TOOL_ERROR,
  MESSAGE_TYPE_TOOL_MESSAGE,
  MESSAGE_TYPE_USER_INPUT,
  TASK_STATUS_COMPLETED,
  TASK_STATUS_FAILED,
  TASK_STATUS_FINAL,
  TOOL_PLAN,
} from '@constants/agentTypes'
import { getFriendlyLlmErrorMessage } from '@constants/errorMessages'
import { ChatFileResponse } from '@api/generated/internal/internalApi'
import { InternalMessage } from '@slices/userMessage'
import { track, eventTypes } from 'torqcloud-ui'

import MarkdownAnswer from './Chat.Markdown'

type TaskMessageBoxProps = {
  chatflowId: string
  message: InternalMessage
  themeStyle?: CSSProperties
  children: ReactNode
}

const TaskMessageBox = ({ chatflowId, message, themeStyle, children }: TaskMessageBoxProps) => {
  if (message.status === TASK_STATUS_COMPLETED) {
    return <Box style={themeStyle}>{children}</Box>
  } else if (message.status === TASK_STATUS_FINAL) {
    return <Box style={themeStyle}>{children}</Box>
  } else if (message.status === TASK_STATUS_FAILED) {
    return (
      <Box
        sx={{
          display: 'flex',
          color: t => t.palette.agentChat.alertRed,
        }}
      >
        <Box
          style={{
            alignItems: 'center',
            display: 'flex',
            paddingRight: '.5rem',
          }}
        >
          <ErrorOutlineOutlinedIcon style={{ fontSize: '1.5rem' }} />
        </Box>
        <MarkdownAnswer downloadName={chatflowId}>{message.value}</MarkdownAnswer>
      </Box>
    )
  }
  return <Box>{children}</Box>
}

function ToolInputSection({ input }: { input: unknown }) {
  const parsedInput = parseToolInput(input)

  return (
    <Box>
      <Typography variant="subsectionHeading" sx={{ mb: '4px' }}>
        Input
      </Typography>
      {isPlainObject(parsedInput) ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Object.entries(parsedInput).map(([key, value]) => (
            <Box key={key}>
              <Typography variant="caption" sx={{ color: t => t.palette.text.secondary }}>
                {key}
              </Typography>
              <ToolInputValue value={value} />
            </Box>
          ))}
        </Box>
      ) : (
        <ToolInputValue value={parsedInput} />
      )}
    </Box>
  )
}

function formatInputSummary(input: unknown): string | null {
  const parsed = parseToolInput(input)
  if (parsed == null) return null

  if (isPlainObject(parsed)) {
    const entries = Object.entries(parsed)
    if (entries.length === 0) return null
    return entries
      .map(([k, v]) => {
        const val =
          Array.isArray(v) && v.every(item => typeof item === 'string')
            ? v.join(', ')
            : isPlainObject(v) || Array.isArray(v)
            ? JSON.stringify(v)
            : String(v)
        return `${k}: ${val}`
      })
      .join(' · ')
  }

  if (Array.isArray(parsed)) {
    return JSON.stringify(parsed)
  }

  return parsed == null ? null : String(parsed)
}

function ToolInputValue({ value }: { value: unknown }) {
  if (isPlainObject(value) || Array.isArray(value)) {
    return <JsonBlock value={value} />
  }

  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
      {value == null ? '' : String(value)}
    </Typography>
  )
}

function JsonBlock({ value }: { value: unknown }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: '12px',
        borderRadius: '8px',
        backgroundColor: t => t.palette.background.default,
        color: t => t.palette.text.primary,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        fontSize: '0.8125rem',
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  )
}

function parseToolInput(input: unknown) {
  if (typeof input !== 'string') {
    return input
  }

  try {
    return JSON.parse(input)
  } catch {
    return input
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export type MessageProps = {
  chatflowId: string
  /** The session's generated files, so a tool result can show the files it produced. */
  sessionFiles?: ChatFileResponse[]
  message: InternalMessage
  // Full message list — used by SubTeammate cards to pull their nested
  // children (messages whose parentToolCallId matches this card's id).
  allMessages?: InternalMessage[]
  totalMessages?: number
  loading?: boolean
  isDialog: boolean
  hasMessageStream: boolean
  index?: number
  themeStyle?: CSSProperties
  avatar?: string
  canSubmitMessageFeedback?: boolean
  onSubmitMessageFeedback?: (feedback: { messageId: string; text?: string | null; value?: MessageFeedbackValue | null }) => Promise<boolean>
  onApproveToolCall?: (message: InternalMessage) => Promise<void>
  onRejectToolCall?: (message: InternalMessage, reason?: string) => Promise<void>
  onCancelSubTeammate?: (message: InternalMessage, reason?: string) => Promise<void>
  onAcceptSubTeammate?: (message: InternalMessage) => Promise<void>
  onSubmitSubTeammateFollowUp?: (message: InternalMessage, followUp: string) => Promise<void>
}

export default function Message({
  chatflowId,
  sessionFiles = [],
  message,
  allMessages,
  isDialog,
  hasMessageStream,
  themeStyle,
  avatar,
  canSubmitMessageFeedback,
  onSubmitMessageFeedback,
  onApproveToolCall,
  onRejectToolCall,
  onCancelSubTeammate,
  onAcceptSubTeammate,
  onSubmitSubTeammateFollowUp,
}: MessageProps) {
  const theme = useTheme()
  const messageAction = getMessageAction(message)
  const title = getTitle(message)
  const [copyAnchorEl, setCopyAnchorEl] = useState<Element | null>(null)
  const [approvalLoading, setApprovalLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [acceptLoading, setAcceptLoading] = useState(false)
  const [followUpText, setFollowUpText] = useState('')
  const [followUpSubmitting, setFollowUpSubmitting] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const [feedbackReadOnly, setFeedbackReadOnly] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')
  const [feedbackValue, setFeedbackValue] = useState<MessageFeedbackValue | null>(null)
  const [feedbackError, setFeedbackError] = useState<string | null>(null)
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
  const rejectReasonRef = useRef<HTMLTextAreaElement>(null)
  const cancelReasonRef = useRef<HTMLTextAreaElement>(null)
  const feedbackTextRef = useRef<HTMLTextAreaElement>(null)
  const openCopyPopover = Boolean(copyAnchorEl)

  const handleCloseCopyPopover = () => {
    setCopyAnchorEl(null)
  }

  const copyMarkdown: MouseEventHandler<HTMLButtonElement> = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      return
    }

    navigator.clipboard.writeText(message.value).catch(error => {
      console.error('Failed to copy text to clipboard:', error)
    })
    track(eventTypes.CHAT_MESSAGE_COPIED, {
      chatflowId,
      messageId: message.id,
      messageType: message.type,
    })
    setCopyAnchorEl(event.currentTarget as Element)
    setTimeout(() => {
      handleCloseCopyPopover()
    }, 1500)
  }

  const openFeedbackDialog = (value: MessageFeedbackValue | null, readOnly = false) => {
    setFeedbackReadOnly(readOnly)
    setFeedbackValue(readOnly ? message.feedback?.value ?? null : value)
    setFeedbackText(readOnly ? message.feedback?.text ?? '' : '')
    setFeedbackError(null)
    setFeedbackDialogOpen(true)
  }

  const closeFeedbackDialog = () => {
    if (feedbackSubmitting) return
    setFeedbackDialogOpen(false)
  }

  const submitFeedback = async () => {
    if (!message.id || !onSubmitMessageFeedback) return

    const normalizedText = feedbackText.trim()
    if (!normalizedText && !feedbackValue) {
      setFeedbackError('Add a comment or choose approve/disapprove before submitting feedback.')
      return
    }

    setFeedbackSubmitting(true)
    try {
      const submitted = await onSubmitMessageFeedback({
        messageId: message.id,
        text: normalizedText || null,
        value: feedbackValue,
      })

      if (submitted) {
        setFeedbackDialogOpen(false)
      }
    } finally {
      setFeedbackSubmitting(false)
    }
  }

  if ((!message.value && message.type !== MESSAGE_TYPE_TOOL_MESSAGE) || (message.type === MESSAGE_TYPE_FINAL_ANSWER && hasMessageStream)) {
    return null
  }

  if (message.type === MESSAGE_TYPE_USER_INPUT) {
    const queueStatusLabel: Partial<Record<NonNullable<InternalMessage['queueStatus']>, string>> = {
      receiving: 'Uploading',
      processing_files: 'Processing files',
      queued: 'Queued',
      dispatching: 'Starting',
      running: 'Generating response',
      waiting_for_approval: 'Waiting for approval',
      waiting_for_background: 'Waiting for background task',
      failed: message.queueError ?? 'Failed',
      cancelled: 'Cancelled',
    }
    const queuedFiles =
      message.queuedFiles ?? message.chatFiles?.map(file => ({ fileName: file.fileName, chatFileId: file.id, status: 'ready' as const }))

    return (
      <Box data-component="message__user-input" sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box
          data-component="message__user-input__content"
          sx={{
            display: 'flex',
            justifySelf: 'self-end',
            alignSelf: 'self-end',
            justifyContent: 'end',
            backgroundColor: t => t.palette.canvasHeader.primaryDark,
            borderRadius: '8px',
            paddingRight: '1rem',
            paddingLeft: '1rem',
            maxWidth: '85%',
          }}
        >
          <Box data-component="message__user-input__markdown">
            <MarkdownAnswer isDialog={isDialog} downloadName={chatflowId}>
              {message.value}
            </MarkdownAnswer>
          </Box>
          <Box data-component="message__user-input__avatar" sx={{ marginLeft: 0, marginTop: 0 }}>
            <Avatar
              src={avatar}
              sx={{
                margin: '.5rem 0 .5rem .75rem',
                height: '1.75rem',
                width: '1.75rem',
                border: '2px solid #FFFFFF',
              }}
            />
          </Box>
        </Box>
        <Box data-component="message__user-input__files" sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {queuedFiles?.map(file => (
            <ChatflowFileChip
              key={file.fileName}
              status={file.status === 'failed' ? 'failure' : file.status === 'ready' ? 'success' : 'processing'}
              name={file.fileName}
              tooltip={file.error}
            />
          ))}
        </Box>
        {message.queueStatus && queueStatusLabel[message.queueStatus] && (
          <Typography
            variant="caption"
            color={message.queueStatus === 'failed' ? 'error' : 'text.secondary'}
            sx={{ alignSelf: 'flex-end' }}
          >
            {queueStatusLabel[message.queueStatus]}
          </Typography>
        )}
      </Box>
    )
  }

  if (
    [
      MESSAGE_TYPE_FINAL_ANSWER,
      MESSAGE_TYPE_FINAL_ANSWER_STREAM,
      MESSAGE_TYPE_INTERMEDIATE_ANSWER,
      MESSAGE_TYPE_INTERMEDIATE_ANSWER_STREAM,
    ].includes(message.type)
  ) {
    const isStreaming = [MESSAGE_TYPE_FINAL_ANSWER_STREAM, MESSAGE_TYPE_INTERMEDIATE_ANSWER_STREAM].includes(message.type)
    const isIntermediate = [MESSAGE_TYPE_INTERMEDIATE_ANSWER, MESSAGE_TYPE_INTERMEDIATE_ANSWER_STREAM].includes(message.type)
    const dataComponent = isIntermediate ? 'message__intermediate-answer' : 'message__final-answer'
    const canShowSubmitFeedback = !message.feedback && canSubmitMessageFeedback && !!message.id && !!onSubmitMessageFeedback
    const feedbackIcon =
      message.feedback?.value === 'approved' ? (
        <ThumbUpAltOutlinedIcon fontSize="small" color="success" />
      ) : message.feedback?.value === 'disapproved' ? (
        <ThumbDownAltOutlinedIcon fontSize="small" color="error" />
      ) : (
        <RateReviewOutlinedIcon fontSize="small" />
      )

    return (
      <>
        <Box
          data-component={dataComponent}
          sx={{
            backgroundColor: t => t.palette.agentChat.darkGray,
            borderRadius: '8px',
            paddingRight: '1rem',
            paddingLeft: '1rem',
            border: '4px solid',
            borderColor: t => t.palette.global.lightGray,
            display: 'block',
            maxWidth: '85%',
            // Prevent layout thrashing during streaming by containing layout calculations
            contain: 'layout style',
            // Smooth height transitions during content growth
            transition: 'height 0.1s ease-out',
          }}
        >
          {!isStreaming && (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '.5rem' }}>
              {isIntermediate ? (
                <>
                  <span style={{ marginRight: '.5rem', fontSize: '1rem' }}>💭</span>
                  <Typography variant="subtitle2">Working on Generating a Response…</Typography>
                </>
              ) : (
                <>
                  <img src="/img/sparkle.svg" alt="Workforce" style={{ width: '1rem', margin: '0 .5rem -.15rem 0' }} />
                  <Typography variant="subtitle2"> Final Output</Typography>
                </>
              )}
              <div style={{ flex: 1 }} />
              {message.feedback ? (
                <Tooltip title="View feedback">
                  <IconButton size="small" color="default" onClick={() => openFeedbackDialog(null, true)}>
                    {feedbackIcon}
                  </IconButton>
                </Tooltip>
              ) : (
                canShowSubmitFeedback && (
                  <>
                    <Tooltip title="Approve response">
                      <IconButton size="small" color="default" onClick={() => openFeedbackDialog('approved')}>
                        <ThumbUpAltOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Disapprove response">
                      <IconButton size="small" color="default" onClick={() => openFeedbackDialog('disapproved')}>
                        <ThumbDownAltOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )
              )}
              <Tooltip title="Copy as Markdown">
                <IconButton size="small" color="default" onClick={copyMarkdown}>
                  <IconClipboard size={18} />
                </IconButton>
              </Tooltip>
              <Popover
                open={openCopyPopover}
                anchorEl={copyAnchorEl}
                onClose={handleCloseCopyPopover}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Typography variant="h6" sx={{ pl: 1, pr: 1, color: 'white', background: theme.palette.success.dark }}>
                  Copied!
                </Typography>
              </Popover>
            </div>
          )}
          <MarkdownAnswer isDialog={isDialog} downloadName={chatflowId}>
            {message.value}
          </MarkdownAnswer>
        </Box>

        <Dialog
          open={feedbackDialogOpen}
          onClose={closeFeedbackDialog}
          fullWidth
          maxWidth="sm"
          TransitionProps={{ onEntered: () => (!feedbackReadOnly ? feedbackTextRef.current?.focus() : undefined) }}
        >
          <DialogTitle sx={{ fontSize: '1rem' }}>{feedbackReadOnly ? 'Message feedback' : 'Provide feedback'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: '8px', pt: 1, mb: 2 }}>
              <Button
                variant={feedbackValue === 'approved' ? 'contained' : 'outlined'}
                color="success"
                startIcon={<ThumbUpAltOutlinedIcon />}
                disabled={feedbackReadOnly || feedbackSubmitting}
                onClick={() => setFeedbackValue('approved')}
              >
                Approve
              </Button>
              <Button
                variant={feedbackValue === 'disapproved' ? 'contained' : 'outlined'}
                color="error"
                startIcon={<ThumbDownAltOutlinedIcon />}
                disabled={feedbackReadOnly || feedbackSubmitting}
                onClick={() => setFeedbackValue('disapproved')}
              >
                Disapprove
              </Button>
            </Box>
            <TextField
              label="Feedback"
              multiline
              rows={4}
              fullWidth
              inputRef={feedbackTextRef}
              value={feedbackText}
              disabled={feedbackReadOnly || feedbackSubmitting}
              onChange={e => {
                setFeedbackText(e.target.value)
                setFeedbackError(null)
              }}
              error={!!feedbackError}
              helperText={feedbackError ?? (feedbackReadOnly ? undefined : 'Comment is optional when a value is selected.')}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeFeedbackDialog} disabled={feedbackSubmitting}>
              {feedbackReadOnly ? 'Close' : 'Cancel'}
            </Button>
            {!feedbackReadOnly && (
              <StyledButton variant="contained" onClick={submitFeedback} disabled={feedbackSubmitting}>
                Submit
              </StyledButton>
            )}
          </DialogActions>
        </Dialog>
      </>
    )
  }

  if (message.type === MESSAGE_TYPE_TASK) {
    return (
      <TaskMessageBox chatflowId={chatflowId} message={message} themeStyle={themeStyle}>
        <Typography
          variant="h6"
          sx={{
            color: 'primary.main',
            mb: '0.5rem',
          }}
        >
          {title}
        </Typography>
        <Typography
          color="textPrimary"
          variant="body2"
          sx={{
            color: 'text.secondary',
            whiteSpace: 'pre-line',
            mb: '1rem',
          }}
        >
          {getTaskBody(message)}
        </Typography>
      </TaskMessageBox>
    )
  }

  if (message.type === MESSAGE_TYPE_API) {
    return (
      <Box
        sx={{
          display: 'flex',
          color: t => t.palette.agentChat.alertRed,
        }}
      >
        <Box
          style={{
            alignItems: 'center',
            display: 'flex',
            paddingRight: '.5rem',
          }}
        >
          <ErrorOutlineOutlinedIcon />
        </Box>
        <MarkdownAnswer downloadName={chatflowId}>{message.value}</MarkdownAnswer>
      </Box>
    )
  }

  if (message.type === MESSAGE_TYPE_TOOL_MESSAGE || message.type === MESSAGE_TYPE_TOOL_ERROR) {
    const generatedFiles = sessionFiles.filter(file => file.chatMessageId && file.chatMessageId === message.id)
    const approvalStatus = message.approval?.status
    const isAwaitingApproval = message.pendingApproval || approvalStatus === 'pending'
    const isRejected = approvalStatus === 'rejected'
    const isApprovedWaiting = approvalStatus === 'approved' && message.status === 'started'
    const hasResult = message.value !== undefined && message.value !== null && message.value !== ''
    // Backend writes structuredOutput as the toolMessage value; the slice parses it
    // into an object on reconciliation, so a canceled SubTeammate result surfaces as
    // value.status === 'canceled'. Treat it as a terminal state distinct from
    // 'completed' so the chip and accent color don't celebrate a cancellation.
    const isSubTeammateCanceled = message.status === 'completed' && isPlainObject(message.value) && message.value.status === 'canceled'
    let progressVariant: 'indeterminate' | 'determinate' = 'determinate'
    switch (message.status) {
      case 'started':
      case undefined:
        progressVariant = 'indeterminate'
        break
    }

    const handleApprove = async () => {
      if (!onApproveToolCall) return
      setApprovalLoading(true)
      try {
        await onApproveToolCall(message)
      } finally {
        setApprovalLoading(false)
      }
    }

    const handleReject = () => {
      if (!onRejectToolCall) return
      setRejectReason('')
      setRejectDialogOpen(true)
    }

    const handleConfirmReject = async () => {
      if (!onRejectToolCall) return
      setRejectDialogOpen(false)
      setApprovalLoading(true)
      try {
        await onRejectToolCall(message, rejectReason || undefined)
      } finally {
        setApprovalLoading(false)
      }
    }

    const handleCancelSubTeammate = () => {
      if (!onCancelSubTeammate) return
      setCancelReason('')
      setCancelDialogOpen(true)
    }

    const handleConfirmCancelSubTeammate = async () => {
      if (!onCancelSubTeammate) return
      setCancelDialogOpen(false)
      setCancelLoading(true)
      track(eventTypes.SUBTEAMMATE_INTERACTION, { chatflowId, action: 'cancel', messageId: message.id })
      try {
        await onCancelSubTeammate(message, cancelReason.trim() || undefined)
      } finally {
        setCancelLoading(false)
      }
    }

    const handleAcceptSubTeammate = async () => {
      if (!onAcceptSubTeammate) return
      setAcceptLoading(true)
      track(eventTypes.SUBTEAMMATE_INTERACTION, { chatflowId, action: 'accept', messageId: message.id })
      try {
        await onAcceptSubTeammate(message)
      } finally {
        setAcceptLoading(false)
      }
    }

    const handleSubmitFollowUp = async () => {
      if (!onSubmitSubTeammateFollowUp) return
      const trimmed = followUpText.trim()
      if (!trimmed) return
      setFollowUpSubmitting(true)
      track(eventTypes.SUBTEAMMATE_INTERACTION, { chatflowId, action: 'followup', messageId: message.id })
      try {
        await onSubmitSubTeammateFollowUp(message, trimmed)
        setFollowUpText('')
      } finally {
        setFollowUpSubmitting(false)
      }
    }

    const isSubTeammate = message.toolPlan?.name === 'sub_teammate'
    const isSubTeammateActive = isSubTeammate && message.status !== 'completed'
    const hasChildFinalOutput = !!message.subTeammateStructuredOutput
    const isPendingUserAcceptance = !!message.pendingUserAcceptance
    // toolPlan.input arrives as a JSON string for toolCall events (only
    // toolMessage values are JSON.parsed in the store); parse it here so the
    // nested instructions bubble renders.
    const subTeammateInstructions = (() => {
      const raw = message.toolPlan?.input
      if (raw == null) return undefined
      const parsed = (() => {
        if (typeof raw === 'object') return raw
        if (typeof raw === 'string') {
          try {
            return JSON.parse(raw)
          } catch {
            return undefined
          }
        }
        return undefined
      })()
      if (parsed && typeof parsed === 'object' && 'instructions' in parsed) {
        return (parsed as { instructions?: unknown }).instructions
      }
      return undefined
    })()
    const nestedChildren = (() => {
      if (!isSubTeammate || !message.id) return []
      const all = (allMessages ?? []).filter(m => m.parentToolCallId === message.id)

      // Collapse duplicate bridged final-answer copies. A child SubTeammate's
      // streamed final answer is bridged onto the parent's channel and
      // reconciled live, but that reconciliation is imperfect (multiple stream
      // placeholders across reconnects, session:state seeding an empty
      // placeholder, parentRunId/value mismatches), so several
      // FINAL_ANSWER / FINAL_ANSWER_STREAM copies of the SAME output can pile up
      // and all render here. Keep one copy per rendered value — dropping empty
      // streaming placeholders and preferring the labeled FINAL_ANSWER over its
      // streaming placeholder — so identical outputs don't show two/three times.
      const isFinalAnswerType = (msg: (typeof all)[number]) =>
        msg.type === MESSAGE_TYPE_FINAL_ANSWER || msg.type === MESSAGE_TYPE_FINAL_ANSWER_STREAM
      const bestFinalByValue = new Map<string, (typeof all)[number]>()
      for (const m of all) {
        if (!isFinalAnswerType(m)) continue
        const trimmed = typeof m.value === 'string' ? m.value.trim() : ''
        if (!trimmed) continue
        const existing = bestFinalByValue.get(trimmed)
        if (!existing || (m.type === MESSAGE_TYPE_FINAL_ANSWER && existing.type !== MESSAGE_TYPE_FINAL_ANSWER)) {
          bestFinalByValue.set(trimmed, m)
        }
      }
      const keptFinals = new Set(bestFinalByValue.values())
      const deduped = all.filter(m => !isFinalAnswerType(m) || keptFinals.has(m))

      // The initial bridged userInput from the child's agentStart has the
      // same content as the parent's `instructions` — which we already render
      // as a nested user-style bubble at the top of the card via
      // subTeammateInstructions. Skip the first userInput whose value matches
      // it to avoid a visual duplicate. Follow-up userInputs render normally.
      const instructionsStr = typeof subTeammateInstructions === 'string' ? subTeammateInstructions : null
      let skippedInitial = false
      return deduped.filter(m => {
        if (!skippedInitial && instructionsStr && m.type === MESSAGE_TYPE_USER_INPUT && m.value === instructionsStr) {
          skippedInitial = true
          return false
        }
        return true
      })
    })()

    const statusLabel = (() => {
      if (isAwaitingApproval) return 'Awaiting approval'
      if (isRejected) return 'Rejected'
      if (isApprovedWaiting) return 'Approved'
      if (message.status === 'failed') return 'Failed'
      if (isSubTeammateCanceled) return 'Canceled'
      if (isPendingUserAcceptance) return 'Awaiting acceptance'
      if (isSubTeammateActive && hasChildFinalOutput) return 'Awaiting follow-up'
      if (message.status === 'completed') return 'Completed'
      return 'Running'
    })()

    return (
      <>
        <Accordion
          data-component="message__tool-message"
          defaultExpanded={message.status !== 'completed' || isAwaitingApproval || isRejected}
          sx={{
            backgroundColor: t => t.palette.agentChat.darkGray,
            borderRadius: '8px',
            border: '3px solid',
            borderColor: t => (message.status === 'failed' ? t.palette.agentChat.alertRed : t.palette.agentChat.darkGray),
            '&::before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="tool"
            id="tool-panel"
            sx={{
              '& .MuiAccordionSummary-content': {
                overflow: 'hidden',
                alignItems: 'center',
                gap: '8px',
              },
            }}
            style={{ display: 'flex' }}
          >
            <Box sx={{ position: 'relative', marginRight: '4px' }}>
              <CircularProgress
                size={30}
                variant={isAwaitingApproval || isRejected ? 'determinate' : progressVariant}
                value={100}
                sx={{
                  position: 'absolute',
                  top: '53%',
                  left: '50%',
                  marginTop: '-18px',
                  marginLeft: '-15px',
                }}
              />
              <BuildCircleIcon
                sx={{
                  borderColor: t => t.palette.global.yellow,
                  borderRadius: '100%',
                  width: '32px',
                  height: '32px',
                }}
              />
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {(() => {
                // For SubTeammate cards, prefer the resolved child Teammate name
                // (e.g. "Weather Bot") over the generic tool label ("Sub Teammate").
                const displayTitle =
                  (isSubTeammate ? message.subTeammateChildChatflowName : undefined) ?? message.toolPlan?.label ?? 'unknown'
                return (
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={displayTitle}
                  >
                    {displayTitle}
                  </Typography>
                )
              })()}
              {(() => {
                const summary = formatInputSummary(message.toolPlan?.input)
                return summary ? (
                  <Typography
                    variant="caption"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: t => t.palette.text.secondary,
                    }}
                    title={summary}
                  >
                    {summary}
                  </Typography>
                ) : null
              })()}
            </Box>
            <Chip size="small" label={statusLabel} />
          </AccordionSummary>
          <AccordionDetails
            sx={{
              color: t => (message.status === 'failed' ? t.palette.agentChat.alertRed : t.palette.global.lightGray),
              backgroundColor: t => t.palette.global.ibBlack,
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              // A wide code block, such as a long JSON result, scrolls inside the card instead of widening it.
              minWidth: 0,
              '& pre': { maxWidth: '100%', overflowX: 'auto' },
            }}
          >
            {isSubTeammate ? (
              <>
                {typeof subTeammateInstructions === 'string' && subTeammateInstructions && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        backgroundColor: t => t.palette.action.selected,
                        borderRadius: '12px',
                        padding: '10px 14px',
                        maxWidth: '80%',
                      }}
                    >
                      <Typography variant="body2">{subTeammateInstructions}</Typography>
                    </Box>
                    <Avatar sx={{ width: 28, height: 28 }} src={avatar} />
                  </Box>
                )}
                {nestedChildren.map((child, idx) => (
                  <Box key={child.id ?? idx} data-message-id={child.id}>
                    <Message
                      chatflowId={chatflowId}
                      message={child}
                      allMessages={allMessages}
                      isDialog={isDialog}
                      hasMessageStream={hasMessageStream}
                      avatar={avatar}
                      canSubmitMessageFeedback={canSubmitMessageFeedback}
                      onSubmitMessageFeedback={onSubmitMessageFeedback}
                      onApproveToolCall={onApproveToolCall}
                      onRejectToolCall={onRejectToolCall}
                      onCancelSubTeammate={onCancelSubTeammate}
                      onAcceptSubTeammate={onAcceptSubTeammate}
                      onSubmitSubTeammateFollowUp={onSubmitSubTeammateFollowUp}
                    />
                  </Box>
                ))}
                {message.subTeammateStructuredOutput && (
                  <Box
                    sx={{
                      backgroundColor: t => t.palette.global.ibBlack,
                      border: '1px solid',
                      borderColor: t => t.palette.action.hover,
                      borderRadius: '8px',
                      padding: '12px',
                    }}
                  >
                    <Typography variant="subsectionHeading" sx={{ mb: '4px' }}>
                      Final Output
                    </Typography>
                    <MarkdownAnswer isDialog={isDialog} downloadName={chatflowId}>
                      {message.subTeammateStructuredOutput.content}
                    </MarkdownAnswer>
                  </Box>
                )}
                {/* Render the toolMessage's value (post-Accept synthetic) when present, so the parent-resumed view still surfaces the final result inline. */}
                {hasResult && message.status === 'completed' && !message.subTeammateStructuredOutput && (
                  <Box>
                    <Typography variant="subsectionHeading" sx={{ mb: '4px' }}>
                      Result
                    </Typography>
                    <MarkdownAnswer isDialog={isDialog} downloadName={chatflowId}>
                      {typeof message.value === 'string' ? message.value : JSON.stringify(message.value)}
                    </MarkdownAnswer>
                  </Box>
                )}
                {isSubTeammateActive && (
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                    {isPendingUserAcceptance && onAcceptSubTeammate && (
                      <IconButton
                        id="subteammate-accept-button"
                        aria-label="Accept SubTeammate result"
                        onClick={handleAcceptSubTeammate}
                        disabled={acceptLoading}
                        sx={{
                          border: '1px solid',
                          borderColor: t => t.palette.success.main,
                          color: t => t.palette.success.main,
                        }}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    )}
                    {onCancelSubTeammate && (
                      <IconButton
                        id="subteammate-cancel-button"
                        aria-label="Cancel SubTeammate"
                        onClick={handleCancelSubTeammate}
                        disabled={cancelLoading}
                        sx={{
                          border: '1px solid',
                          borderColor: t => t.palette.error.main,
                          color: t => t.palette.error.main,
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    <TextField
                      id="subteammate-followup-input"
                      placeholder={
                        hasChildFinalOutput
                          ? `Message ${message.subTeammateChildChatflowName ?? message.toolPlan?.label ?? 'SubTeammate'}`
                          : 'Waiting for SubTeammate…'
                      }
                      value={followUpText}
                      onChange={e => setFollowUpText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          e.stopPropagation()
                          if (hasChildFinalOutput && !followUpSubmitting) {
                            void handleSubmitFollowUp()
                          }
                        }
                      }}
                      disabled={!hasChildFinalOutput || followUpSubmitting}
                      fullWidth
                      size="small"
                      sx={{ flexGrow: 1 }}
                    />
                    <IconButton
                      id="subteammate-followup-send-button"
                      aria-label="Send follow-up to SubTeammate"
                      onClick={() => void handleSubmitFollowUp()}
                      disabled={!hasChildFinalOutput || followUpSubmitting || !followUpText.trim()}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </>
            ) : (
              <>
                <ToolInputSection input={message.toolPlan?.input} />
                {hasResult && (
                  <Box>
                    <Typography variant="subsectionHeading" sx={{ mb: '4px' }}>
                      Result
                    </Typography>
                    <MarkdownAnswer isDialog={isDialog} downloadName={chatflowId}>
                      {message.value}
                    </MarkdownAnswer>
                  </Box>
                )}
                {isRejected && message.approval?.reason && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: '4px' }}>
                      Reviewer note
                    </Typography>
                    <Typography variant="body2">{message.approval.reason}</Typography>
                  </Box>
                )}
                {isAwaitingApproval && (
                  <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button variant="outlined" color="error" disabled={approvalLoading} onClick={handleReject}>
                      Reject
                    </Button>
                    <Button variant="contained" disabled={approvalLoading} onClick={handleApprove}>
                      Accept
                    </Button>
                  </Box>
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>
        {!isEmpty(generatedFiles) && (
          <Box
            data-component="message__tool-message__files"
            // Matches the message list's 16px gap so the chip sits evenly between its neighbours.
            sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '16px' }}
          >
            {generatedFiles.map(file => (
              <ChatflowFileChip key={file.id} name={file.fileName} variant="dark-grey" href={file.url} />
            ))}
          </Box>
        )}

        <Dialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          TransitionProps={{ onEntered: () => rejectReasonRef.current?.focus() }}
        >
          <DialogTitle sx={{ fontSize: '1rem' }}>Reject tool call</DialogTitle>
          <DialogContent>
            <Box id="reject_dialog__container" sx={{ pt: 1 }}>
              <TextField
                id="reject_dialog__reason"
                label="Reason (optional)"
                multiline
                rows={3}
                fullWidth
                inputRef={rejectReasonRef}
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    void handleConfirmReject()
                  }
                }}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" sx={{ color: t => t.palette.text.secondary }}>
                Enter to reject · Shift+Enter for new line
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions id="reject_dialog__actions">
            <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <StyledButton variant="contained" onClick={handleConfirmReject}>
              Reject
            </StyledButton>
          </DialogActions>
        </Dialog>

        <Dialog
          id="cancel-subteammate-dialog"
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          TransitionProps={{ onEntered: () => cancelReasonRef.current?.focus() }}
        >
          <DialogTitle sx={{ fontSize: '1rem' }}>Cancel SubTeammate</DialogTitle>
          <DialogContent>
            <Box id="cancel-subteammate-dialog-container" sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1.5, color: t => t.palette.text.secondary }}>
                Tell the parent Teammate why this approach should be abandoned so it can try something different.
              </Typography>
              <TextField
                id="cancel-subteammate-reason-input"
                label="Reason (optional)"
                multiline
                rows={3}
                fullWidth
                inputRef={cancelReasonRef}
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    void handleConfirmCancelSubTeammate()
                  }
                }}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" sx={{ color: t => t.palette.text.secondary }}>
                Enter to cancel · Shift+Enter for new line
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions id="cancel-subteammate-dialog-actions">
            <Button id="cancel-subteammate-dismiss-button" onClick={() => setCancelDialogOpen(false)}>
              Keep running
            </Button>
            <StyledButton id="cancel-subteammate-confirm-button" variant="contained" color="error" onClick={handleConfirmCancelSubTeammate}>
              Cancel SubTeammate
            </StyledButton>
          </DialogActions>
        </Dialog>
      </>
    )
  }

  if (messageAction && messageAction.tool !== TOOL_PLAN) {
    return null
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          color: t => (message.type === MESSAGE_TYPE_LLM_ERROR ? t.palette.agentChat.alertRed : t.palette.agentChat.infoBlue),
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            paddingRight: '.5rem',
          }}
        >
          {message.type === MESSAGE_TYPE_LLM_ERROR ? <ErrorOutlineOutlinedIcon /> : <InfoCircled style={{ fontSize: '1.3rem' }} />}
        </Box>
        {!isEmpty(title) && (
          <Typography
            variant="h6"
            sx={{
              color: 'primary.main',
              mb: '0',
              display: 'block',
            }}
          >
            {title}
          </Typography>
        )}
        <MarkdownAnswer isDialog={isDialog} downloadName={chatflowId}>
          {message.type === MESSAGE_TYPE_LLM_ERROR ? getFriendlyLlmErrorMessage(message.value, message.errorCode) : message.value}
        </MarkdownAnswer>
      </Box>
    </>
  )
}
